import type { AxiosError } from 'axios';
import type { FormikErrors, FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import { useHistory, useLocation } from 'react-router';
import * as Yup from 'yup';

import { DevOnlyRegistrationInfo } from 'src/app/components/Elements/DevOnly/DevOnlyRegistrationInfo';
import LinkWithQuery from 'src/app/components/Elements/LinkWithQuery';
import { FormikTextField } from 'src/app/components/Form/FormikTextField/FormikTextField';
import { Step } from 'src/app/components/Pages/GetStartedNew/StepLayout/Step';
import { useCreateLead } from 'src/app/hooks/data/lead';
import { useCheckEmailAvailability } from 'src/app/hooks/data/user';
import { useGetStartedPersistentData } from 'src/app/hooks/feature_data/useGetStartedPersistentData';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { getStartedNew, getStartedNewCaptcha, getStartedNewTransferIntent, login } from 'src/app/router/paths';
import type { SegmentTrackType } from 'src/app/state/actions';

import type { ProgressStep, STEP_KEY } from '../../StepLayout/consts';
import { ButtonContainer, LegalMessage, StyledButton } from '../../styles';

import { TermsOfService } from './TermsOfService';

type FormValues = {
  email: string;
};
export interface SubmitProps {
  handleNextClick: () => void;
  onCreateLead: ({ zipCode, email, source }: { zipCode: string; email: string; source: string }) => void;
  onEmailCheck: (email: string) => Promise<any>;
  onSetOnboardingFormValues: ({ email }: { email: string }) => void;
  segmentTrack: SegmentTrackType;
  email: string;
  zipCode: string;
  onAddOnboardingStepAfterGivenStepKey: (arg0: STEP_KEY, arg1: ProgressStep) => void;
}

export const SignInLink = () => (
  <>
    There is already an account with this email. <LinkWithQuery to={login.toPath()}>Sign in</LinkWithQuery>
  </>
);

export const ContactStep = () => {
  const history = useHistory();
  const { state: locationState } = useLocation<{ errorCode: string }>();

  const { mutateAsync: createLead } = useCreateLead();
  const { mutateAsync: checkEmailAvailability } = useCheckEmailAvailability();

  useSegmentEvent('viewOnboardingContactInfo', { shouldTrackOnMount: true, payload: { contact_type: 'email' } });
  const trackJoinDeliveryAreaWailist = useSegmentEvent('captchaFailed', { shouldTrackOnMount: false });
  const trackCreateLead = useSegmentEvent('createLead', { shouldTrackOnMount: false });

  const { getStartedPersistentData, setGetStartedPersistentData } = useGetStartedPersistentData();

  const handleSubmit = async (values: any, formikHelpers: FormikHelpers<any>) => {
    const email = (values.email as string)?.toLowerCase().trim();
    formikHelpers.setSubmitting(true);

    setGetStartedPersistentData({ email, zipCode: getStartedPersistentData ? getStartedPersistentData.zipCode : '' });

    const grecaptcha = typeof window !== 'undefined' ? window.grecaptcha : undefined;

    try {
      grecaptcha?.ready(async () => {
        const captchaToken = await grecaptcha.execute('6LcyrVEfAAAAACITXKWQOR_9gP5otCO406KbZEvu', {
          action: 'email_check',
        });

        try {
          await checkEmailAvailability({ email, captchaToken });

          trackCreateLead();
          // Don't wait to create lead to finish.
          createLead({
            zipCode:
              getStartedPersistentData && getStartedPersistentData.zipCode ? getStartedPersistentData.zipCode : '',
            email: values.email,
            source: 'onboarding_stay_in_touch',
          });

          return history.push(getStartedNewTransferIntent.route);
        } catch (error) {
          const axiosError = error as AxiosError;

          if (axiosError.response?.status === 403) {
            trackJoinDeliveryAreaWailist({ type: 'Invisible' });
            return history.push(getStartedNewCaptcha.route);
          }

          const errorMessage = {
            email: axiosError.response?.status === 400 ? <SignInLink /> : 'Error validating email',
          } as FormikErrors<FormValues>;
          formikHelpers.setErrors(errorMessage);
        }
      });
    } catch (error) {
      trackJoinDeliveryAreaWailist({ type: 'Invisible' });
      throw new Error('Could not check email, failed to load google recaptcha');
    }

    formikHelpers.setSubmitting(false);
  };

  return (
    <Step
      title={`Great! Add your email address`}
      text={`Enter your email so we can save your progress`}
      customHandlePrevText={'Delivery'}
      handlePrev={() => history.push(getStartedNew.route)}
    >
      <Formik
        initialValues={{ email: getStartedPersistentData?.email ?? '' }}
        isInitialValid={false}
        validateOnMount={true}
        onSubmit={handleSubmit}
        validationSchema={Yup.object().shape({
          email: Yup.string().email('Please enter a valid email address.').required(),
        })}
      >
        {({ isValid, isSubmitting }) => {
          return (
            <Form>
              <FormikTextField
                name="email"
                label={{ content: '', isHidden: false }}
                type="text"
                error={locationState?.errorCode === 'existing_email' ? <SignInLink /> : ''}
              />
              <ButtonContainer smallMarginTop={true}>
                <StyledButton size="medium" disabled={!isValid || isSubmitting} type="submit" style={'capsuleBlue50'}>
                  Continue
                </StyledButton>
              </ButtonContainer>
              <LegalMessage>
                <TermsOfService />
              </LegalMessage>
            </Form>
          );
        }}
      </Formik>

      <DevOnlyRegistrationInfo />
    </Step>
  );
};
